import { useEffect, useState, useCallback, useRef } from "react";
import api from "../api";
import { useConfirm } from "../components/ConfirmDialog";

const SOURCE_LABEL = {
  initial: { label: "Initial", cls: "src-initial" },
  import: { label: "Import", cls: "src-import" },
  revert: { label: "Revert", cls: "src-revert" },
  "pre-import": { label: "Auto · pre-import", cls: "src-auto" },
  "pre-revert": { label: "Auto · pre-revert", cls: "src-auto" },
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  );
}

function CountDiff({ current, incoming }) {
  if (!current || !incoming) return null;
  const rows = [
    ["Workstreams", "workstreams"],
    ["Tasks", "tasks"],
    ["Opportunity threads", "threads"],
    ["Discussion topics", "discussionTopics"],
  ];
  return (
    <div className="ver-diff">
      <div className="ver-diff-head">
        <span>Item</span>
        <span>Current</span>
        <span>Incoming</span>
        <span>Δ</span>
      </div>
      {rows.map(([label, key]) => {
        const c = current[key] || 0;
        const i = incoming[key] || 0;
        const delta = i - c;
        const deltaClass = delta > 0 ? "delta-up" : delta < 0 ? "delta-down" : "delta-flat";
        return (
          <div className="ver-diff-row" key={key} data-testid={`ver-diff-${key}`}>
            <span>{label}</span>
            <span className="ver-num">{c}</span>
            <span className="ver-num">{i}</span>
            <span className={`ver-delta ${deltaClass}`}>
              {delta > 0 ? `+${delta}` : delta}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function VersionRow({ v, isCurrent, onRevert, onView }) {
  const src = SOURCE_LABEL[v.source] || { label: v.source, cls: "src-other" };
  return (
    <div
      className={`ver-row ${isCurrent ? "current" : ""}`}
      data-testid={`ver-row-${v.versionId}`}
    >
      {isCurrent && (
        <span className="ver-current-pill" data-testid="ver-current-pill">CURRENT</span>
      )}
      <div className="ver-row-body">
        <div className="ver-row-head">
          <span className={`ver-src-pill ${src.cls}`}>{src.label}</span>
          <span className="ver-vid">{v.versionId.slice(0, 8)}</span>
          <span className="ver-time">{formatDate(v.createdAt)}</span>
        </div>
        {v.note && <div className="ver-note">{v.note}</div>}
        <div className="ver-counts">
          {v.counts.workstreams} workstreams · {v.counts.tasks} tasks ·{" "}
          {v.counts.threads} threads · {v.counts.discussionTopics} discussion topics
        </div>
      </div>
      <div className="ver-row-actions">
        <button
          className="btn btn-sm"
          onClick={() => onView(v.versionId)}
          data-testid={`ver-row-${v.versionId}-view`}
        >
          View
        </button>
        {!isCurrent && (
          <button
            className="btn btn-sm btn-revert"
            onClick={() => onRevert(v)}
            data-testid={`ver-row-${v.versionId}-revert`}
          >
            Revert here
          </button>
        )}
      </div>
    </div>
  );
}

function ImportPreviewModal({ preview, fileName, onConfirm, onCancel, busy }) {
  if (!preview) return null;
  return (
    <div className="confirm-backdrop" onClick={onCancel} data-testid="ver-import-preview-backdrop">
      <div
        className="confirm-card"
        style={{ maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="ver-import-preview-dialog"
      >
        <div className="confirm-title">
          {preview.valid ? "Confirm import" : "Cannot import this file"}
        </div>
        <div className="confirm-message">
          File: <strong>{fileName}</strong>
        </div>

        {!preview.valid ? (
          <div className="ver-errors" data-testid="ver-import-errors">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {(preview.errors || []).map((e, i) => (
                <li key={`${e}-${i}`}>{e}</li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="confirm-message" style={{ marginTop: 0 }}>
              The current live state will be auto-saved as a backup before the
              import. You can revert at any time from the version history.
            </div>
            <CountDiff current={preview.current} incoming={preview.incoming} />
          </>
        )}

        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onCancel} data-testid="ver-import-cancel">
            Cancel
          </button>
          {preview.valid && (
            <button
              className="confirm-primary"
              onClick={onConfirm}
              disabled={busy}
              data-testid="ver-import-confirm"
            >
              {busy ? "Importing…" : "Import & swap live state"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionDetailModal({ vid, onClose }) {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    if (!vid) return;
    let mounted = true;
    api.get(`/versions/${vid}`).then(({ data }) => {
      if (mounted) setVersion(data);
    });
    return () => {
      mounted = false;
    };
  }, [vid]);

  if (!vid) return null;
  const src = version ? SOURCE_LABEL[version.source] : null;
  return (
    <div className="confirm-backdrop" onClick={onClose} data-testid="ver-detail-backdrop">
      <div
        className="confirm-card"
        style={{ maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="ver-detail-dialog"
      >
        <div className="confirm-title">Version details</div>
        {!version ? (
          <div className="confirm-message">Loading…</div>
        ) : (
          <>
            <div className="ver-detail-grid">
              <span>Version ID</span>
              <span className="ver-mono">{version.versionId}</span>
              <span>Created</span>
              <span>{formatDate(version.createdAt)}</span>
              <span>Source</span>
              <span><span className={`ver-src-pill ${src?.cls}`}>{src?.label}</span></span>
              <span>Note</span>
              <span>{version.note || <em style={{ color: "var(--text-faint)" }}>none</em>}</span>
              <span>Workstreams</span>
              <span className="ver-num">{version.counts.workstreams}</span>
              <span>Tasks</span>
              <span className="ver-num">{version.counts.tasks}</span>
              <span>Opportunity threads</span>
              <span className="ver-num">{version.counts.threads}</span>
              <span>Discussion topics</span>
              <span className="ver-num">{version.counts.discussionTopics}</span>
            </div>
          </>
        )}
        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onClose} data-testid="ver-detail-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Versions({ goHome, refresh }) {
  const confirm = useConfirm();
  const [list, setList] = useState({ items: [], current: null, cap: 5 });
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [previewPayload, setPreviewPayload] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [detailVid, setDetailVid] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/versions");
      setList(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file later
    if (!f) return;
    setPreviewFileName(f.name);
    setPreviewPayload(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/versions/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // If valid, also read the file locally so we can POST the payload on confirm.
      if (data.valid) {
        const text = await f.text();
        setPreviewPayload(JSON.parse(text));
      }
    } catch (err) {
      setPreview({
        valid: false,
        errors: [err?.response?.data?.detail || err.message || "Preview failed"],
      });
    }
  };

  const doImport = async () => {
    if (!previewPayload) return;
    setImportBusy(true);
    try {
      await api.post("/versions/import", {
        payload: previewPayload,
        note: previewFileName ? `from ${previewFileName}` : "",
      });
      setPreview(null);
      setPreviewPayload(null);
      setPreviewFileName("");
      await load();
      await refresh?.();
    } catch (err) {
      // Show error in the preview dialog
      setPreview({
        valid: false,
        errors: [err?.response?.data?.detail || err.message || "Import failed"],
      });
    } finally {
      setImportBusy(false);
    }
  };

  const doRevert = async (v) => {
    const ok = await confirm({
      title: `Revert to this version?`,
      body: `This will swap the live state to the snapshot from ${formatDate(v.createdAt)}. The current live state will be auto-saved as a backup, so this revert can itself be undone.`,
      confirmText: "Revert to this version",
      danger: true,
    });
    if (!ok) return;
    await api.post(`/versions/${v.versionId}/revert`);
    await load();
    await refresh?.();
  };

  return (
    <div className="view" data-testid="versions-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="ver-back-to-home">← Home</a>
        {" / "}
        Version history
      </div>

      <div className="ver-head" style={{ marginTop: 16 }}>
        <div>
          <div className="sec-head" style={{ margin: 0 }}>Version history</div>
          <div className="sec-desc">
            Every import or revert creates a new snapshot. The {list.cap}-most-recent
            snapshots are retained; the current and immediately-prior version are
            always protected from pruning.
          </div>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={onFileChange}
            style={{ display: "none" }}
            data-testid="ver-import-file-input"
          />
          <button
            className="btn btn-primary"
            onClick={() => fileRef.current?.click()}
            data-testid="ver-import-button"
          >
            + Import snapshot
          </button>
        </div>
      </div>

      {loading ? (
        <div className="disc-empty">Loading…</div>
      ) : list.items.length === 0 ? (
        <div className="disc-empty">No versions yet.</div>
      ) : (
        <div className="ver-list">
          {list.items.map((v) => (
            <VersionRow
              key={v.versionId}
              v={v}
              isCurrent={v.versionId === list.current}
              onRevert={doRevert}
              onView={setDetailVid}
            />
          ))}
        </div>
      )}

      <ImportPreviewModal
        preview={preview}
        fileName={previewFileName}
        onConfirm={doImport}
        onCancel={() => {
          setPreview(null);
          setPreviewPayload(null);
        }}
        busy={importBusy}
      />

      <VersionDetailModal vid={detailVid} onClose={() => setDetailVid(null)} />
    </div>
  );
}
